import { Helmet } from 'react-helmet-async';
import { Phone, Video, FileCheck, Users, CheckCircle, Home } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import getCanonicalUrl from '../utils/canonical';

const ComeFunziona = () => {
  const steps = [
    {
      icon: Phone,
      number: '01',
      title: 'Colloquio Conoscitivo',
      description: 'Un primo contatto telefonico o in videochiamata per conoscerci. Parliamo di voi, delle vostre aspettative e di come possiamo aiutarvi. Nessun impegno, solo ascolto.'
    },
    {
      icon: FileCheck,
      number: '02',
      title: 'Analisi di Compatibilità',
      description: 'Studiamo il vostro profilo, i vostri valori, le vostre aspirazioni. Verifichiamo documenti e serietà. Definiamo insieme il tipo di persona che cercate.'
    },
    {
      icon: Users,
      number: '03',
      title: 'Presentazioni Mirate',
      description: 'Vi presentiamo solo profili compatibili, selezionati con cura. Niente sprechi di tempo: ogni presentazione ha un senso e una motivazione precisa.'
    },
    {
      icon: CheckCircle,
      number: '04',
      title: 'Supporto Continuo',
      description: 'Non vi lasciamo soli. Dopo ogni incontro raccogliamo feedback, affiniamo la ricerca e vi accompagniamo fino al risultato. Siamo al vostro fianco.'
    }
  ];

  return (
    <div className="page-container">
      <Helmet>
        <title>Il Metodo | Agenzia per Single Napoli e Roma | Officine del Cuore</title>
        <meta name="description" content="Come funziona: Agenzia Matrimoniale Officine del Cuore: Iscrizione in sede oppure al telefono e in videochiamata. Agenzia di incontri seri a Napoli, Caserta, Roma" />
        <link rel="canonical" href={getCanonicalUrl('come-funziona')} />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero come-funziona-hero" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_officine-cuore/artifacts/7reuvgun_Gemini_Generated_Image_h0ij6uh0ij6uh0ijmergellinaWebp.webp)'}}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="page-title fade-in">Il Nostro Metodo</h1>
          <p className="page-subtitle fade-in-delay">
            Un percorso semplice, discreto e personalizzato
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="section-elegant">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="section-title text-center mb-12">
            Scopri Come Funziona
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {steps.map((step) => (
              <Card key={step.number} className="step-card">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="step-number">{step.number}</div>
                    <div className="flex-1">
                      <div className="step-icon-wrapper">
                        <step.icon className="step-icon" />
                      </div>
                      <h3 className="step-title">{step.title}</h3>
                      <p className="step-description">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Remote Registration Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="remote-registration-card">
            <div className="flex items-center justify-center mb-6">
              <Video className="text-gold mr-3" size={40} />
              <h2 className="section-title mb-0">Possibilità di Iscrizione in Presenza o a Distanza con Video-Chiamata.</h2>
            </div>
            <div className="prose-elegant">
              <p className="lead-text">
                Questa alternativa a distanza nasce dall'ascolto dei clienti, i quali per mancanza di tempo, 
                distanza dalla sede o per un naturale imbarazzo, preferiscono non recarsi in agenzia.
              </p>
              <p>
                Ci siamo resi conto che l'incontro in sede, <strong>pur restando possibile</strong>, non migliora la qualità del servizio. 
                Crediamo che <strong>il vero valore non è nel luogo, ma nell'attenzione, nel dialogo autentico</strong>.
              </p>
              <p>
                Per questo abbiamo scelto di promuovere l'iscrizione a distanza, eliminando solo ciò che non aggiunge valore e di conseguenza siamo riusciti a 
                <strong> ridurre il costo per i nostri clienti</strong>, perché vengono eliminati aspetti organizzativi 
                che non incidono sui risultati, restando invariati il metodo, la professionalità, la selezione accurata 
                e l'attenzione umana che ci contraddistingue.
              </p>
              
              <div className="highlight-box">
                <h3 className="text-burgundy font-playfair text-xl mb-4">
                  L'iscrizione a distanza è una scelta di qualità e un risparmio economico per i nostri clienti
                </h3>
                <p className="mb-0">
                  Un'evoluzione del servizio. Un risparmio giusto, perché non viene tolto nulla a ciò che conta davvero.
                </p>
              </div>

              <h3 className="text-burgundy font-playfair text-2xl mt-8 mb-4">
                Vantaggi dell'Iscrizione a Distanza
              </h3>
              
              <div className="benefits-list">
                <div className="benefit-item">
                  <Home className="text-gold flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-burgundy mb-1">Comodamente da casa</h4>
                    <p>La persona è più rilassata, naturale e a suo agio. Niente formalità o disagio.</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <CheckCircle className="text-gold flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-burgundy mb-1">Risparmio di tempo</h4>
                    <p>Non serve spostarsi fisicamente, nessuna attesa. Il colloquio avviene quando siete comodi.</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <FileCheck className="text-gold flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-burgundy mb-1">Risparmio economico</h4>
                    <p>Meno costi organizzativi significano un processo più snello e tariffe più accessibili.</p>
                  </div>
                </div>
              </div>

              <p className="emphasis-text">
                L'iscrizione a distanza consente di avvicinarsi a Officine del Cuore in modo discreto, 
                sereno e naturale, senza rinunciare alla qualità del servizio. Il risparmio è una scelta etica, 
                non commerciale. <strong>Non vi facciamo pagare ciò che non serve.</strong>
              </p>

              <div className="verification-box">
                <h4 className="font-semibold text-burgundy mb-2">Verifica Documenti in Sicurezza</h4>
                <p className="text-sm mb-0">
                  Durante la videochiamata, procediamo con la verifica dei documenti in tempo reale, 
                  garantendo la stessa sicurezza e serietà dell'incontro in sede.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section className="section-elegant">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="section-title mb-8">
            Qualità, Non Quantità
          </h2>
          <div className="prose-elegant">
            <p>
              Il nostro obiettivo non è farvi incontrare decine di persone. È farvi incontrare 
              <strong> la persona giusta</strong>. Per questo ogni presentazione è studiata, motivata e pensata.
            </p>
            <p>
              Non lavoriamo con numeri, ma con storie. Ogni iscritto è una persona unica, 
              con una sensibilità e un percorso diverso. E voi meritate lo stesso rispetto.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-alt">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="cta-title">
            Iniziamo Insieme Questo Percorso
          </h2>
          <p className="cta-description">
            Il primo colloquio è gratuito e senza impegno. Parliamo delle vostre aspettative 
            e valutiamo insieme se possiamo essere le persone giuste per aiutarvi.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Link to="/contatti">
              <Button size="lg" className="btn-primary btn-large">
                Richiedi un Colloquio Riservato
              </Button>
            </Link>
            <a href="tel:+393292626152">
              <Button size="lg" className="btn-primary btn-large">
                Chiama Ora
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComeFunziona;
